/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useRetracked } from 'js/lib/retracked';

import { MenuList, Popover } from '@coursera/cds-core';
import {
  CloseIcon,
  ExternalLinkIcon,
  MaximizeIcon,
  MinimizeIcon,
  MoreActionsIcon,
  NewChatIcon,
  SearchIcon,
  SettingsIcon,
  SwapIcon,
} from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachIconButton } from 'bundles/ai-coach-platform/components/building-blocks';
import type { Props as ChatHeaderProps } from 'bundles/ai-coach-platform/components/patterns/chat/common/ChatHeader';
import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import _t from 'i18n!nls/ai-coach-platform';

const styles = {
  root: css`
    display: none;

    @container coach-chat (max-height: 480px) {
      display: block;
    }
  `,
};

type Props = ChatHeaderProps;

/**
 * Compact menu containing all ChatHeader action buttons.
 * Visible only when the coach-chat container height is ≤ 480px (e.g. at high browser zoom levels),
 * where the ChatHeader is hidden to preserve vertical space.
 */
const ChatHeaderActionsMenu = (props: Props) => {
  const {
    onClose,
    handleSettingsClick,
    showSettings,
    showClose,
    showStartNewChat = true,
    handleNewSession,
    loading = false,
    showFullScreenButton = false,
    toggleFullScreen,
    isFullScreen = false,
    showPositionToggle = false,
    handlePositionToggleClick,
    chatPopupButton,
    handlePopupButtonClick,
    showSearchButton = false,
    enableContinuousChat = false,
  } = props;

  const [anchorElement, setAnchorElement] = React.useState<HTMLButtonElement | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const trackRetracked = useRetracked();
  const trackingData = useCoachTrackingContext()?.data;
  const track = useTracker();
  const { enterSearchMode } = useCoachChatContext();

  const open = Boolean(anchorElement);
  const close = () => setAnchorElement(null);

  const handleCoachSettingsClick = () => {
    close();
    handleSettingsClick?.();
    track('interact_coach', {
      coachInteractionType: 'coach_settings_open',
      coach: { mode: trackingData?.mode },
    });

    // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
    track('click_coach_element', {
      clickElementType: 'open_settings',
      coach: { mode: trackingData?.mode },
    });
  };

  const handleNewSessionClick = () => {
    close();
    if (!enableContinuousChat) {
      trackRetracked({
        trackingData: { timestamp: Date.now() },
        trackingName: 'ai_course_coach_chat_header_new_session_button',
        action: 'click',
      });
      track('interact_coach', {
        coachInteractionType: 'start_new_chat_session',
        coach: { mode: trackingData?.mode },
        ...(trackingData?.courseId && { product: { type: 'course', id: trackingData.courseId as string } }),
        ...(trackingData?.itemId && { courseItem: { id: trackingData.itemId as string } }),
      });

      // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
      track('click_coach_element', {
        clickElementType: 'start_new_chat',
        coach: { mode: trackingData?.mode },
        ...(trackingData?.courseId && { product: { type: 'course', id: trackingData.courseId as string } }),
        ...(trackingData?.itemId && { courseItem: { id: trackingData.itemId as string } }),
      });
    }
    handleNewSession?.();
  };

  const fullScreenButtonLabel = isFullScreen ? _t('Collapse') : _t('Expand');

  return (
    <div css={styles.root}>
      <CoachIconButton
        ref={buttonRef}
        size="small"
        aria-label={_t('More chat options')}
        aria-haspopup="menu"
        aria-expanded={open}
        tooltip={_t('More chat options')}
        tooltipPlacement="top"
        onClick={(e) => setAnchorElement(e.currentTarget as HTMLButtonElement)}
        icon={<MoreActionsIcon color="default" />}
        disabled={loading}
        data-testid="coach-more-actions"
      />

      <Popover open={open} anchorElement={anchorElement} onClose={close} returnFocusRef={buttonRef}>
        <Popover.Body>
          <MenuList>
            {showPositionToggle && (
              <MenuList.Item
                role="menuitem"
                prefix={
                  <SwapIcon
                    css={css`
                      transform: rotate(90deg);
                    `}
                  />
                }
                disabled={loading}
                onClick={() => {
                  close();
                  handlePositionToggleClick?.();
                }}
              >
                {_t('Swap position')}
              </MenuList.Item>
            )}
            {chatPopupButton && (
              <MenuList.Item
                role="menuitem"
                prefix={<ExternalLinkIcon />}
                disabled={loading}
                onClick={() => {
                  close();
                  handlePopupButtonClick?.();
                }}
              >
                {chatPopupButton === 'in' ? _t('Pop-in chat') : _t('Pop-out chat')}
              </MenuList.Item>
            )}
            {showSearchButton && (
              <MenuList.Item
                role="menuitem"
                prefix={<SearchIcon />}
                disabled={loading}
                onClick={() => {
                  close();
                  enterSearchMode();
                }}
              >
                {_t('Find in chat')}
              </MenuList.Item>
            )}
            {showStartNewChat && (
              <MenuList.Item
                role="menuitem"
                prefix={<NewChatIcon />}
                disabled={loading}
                onClick={handleNewSessionClick}
              >
                {_t('New chat')}
              </MenuList.Item>
            )}
            {showSettings && (
              <MenuList.Item
                role="menuitem"
                prefix={<SettingsIcon />}
                disabled={loading}
                onClick={handleCoachSettingsClick}
              >
                {_t('Settings')}
              </MenuList.Item>
            )}
            {showFullScreenButton && (
              <MenuList.Item
                role="menuitem"
                prefix={isFullScreen ? <MinimizeIcon /> : <MaximizeIcon />}
                onClick={() => {
                  close();
                  toggleFullScreen?.();
                }}
              >
                {fullScreenButtonLabel}
              </MenuList.Item>
            )}
            {showClose && (
              <MenuList.Item
                role="menuitem"
                prefix={<CloseIcon />}
                onClick={() => {
                  close();
                  trackRetracked({
                    trackingData: { timestamp: Date.now() },
                    trackingName: 'ai_course_coach_chat_header_close_button',
                    action: 'click',
                  });
                  onClose();
                }}
              >
                {_t('Close chat')}
              </MenuList.Item>
            )}
          </MenuList>
        </Popover.Body>
      </Popover>
    </div>
  );
};

export default ChatHeaderActionsMenu;
